import { Routes } from '@angular/router';
import { authGuard } from './guards/auth.guard';
import { EulaGuard } from './guards/eula.guard';
import { offlineModeBlockGuard } from './guards/offline-mode.guard';
import { piledOperatorRoleGuard } from './guards/piled-operator-role.guard';
import { projectAccessGuard } from './guards/project-access.guard';
import {
  pullTestingOperatorRoleGuard,
  pullTestingReportRoleGuard,
  pullTestingSetupRoleGuard,
} from './guards/pull-testing-report-role.guard';
import { LoginPage } from './pages/login/login.page';

export const routes: Routes = [
  {
    path: 'home',
    loadComponent: () => import('./home/home.page').then((m) => m.HomePage),
  },
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full',
  },
  {
    path: 'login',
    component: LoginPage
  },
  {
    path: 'first-time-password',
    loadComponent: () => import('./pages/first-time-password/first-time-password.page').then(m => m.FirstTimePasswordPage)
  },
  // Shell: one persistent header + router-outlet for all authenticated app routes
  {
    path: '',
    loadComponent: () => import('./app-shell/app-shell.component').then(m => m.AppShellComponent),
    canActivate: [authGuard, EulaGuard, offlineModeBlockGuard],
    children: [
      {
        path: 'landing',
        loadChildren: () => import('./pages/landing/landing.module').then(m => m.LandingPageModule),
      },
      {
        path: 'change-password',
        loadComponent: () => import('./pages/change-password/change-password.page').then(m => m.ChangePasswordPage),
      },
      {
        path: 'user/profile',
        loadComponent: () => import('./pages/user/user-profile.page').then(m => m.UserProfilePage),
      },
      {
        path: 'open-source-licenses',
        loadComponent: () => import('./pages/open-source-licenses/open-source-licenses.page').then(m => m.OpenSourceLicensesPage),
      },
      {
        path: 'job-setup',
        loadComponent: () => import('./pages/job-setup/job-setup.page').then(m => m.JobSetupPage),
      },
      {
        path: 'team-management',
        loadComponent: () => import('./pages/team-management/team-management.page').then(m => m.TeamManagementPage),
      },
      {
        path: 'project/:projectID',
        canActivate: [projectAccessGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./modules/project-module-select/project-module-select/project-module-select.component').then(
                m => m.ProjectModuleSelectComponent
              ),
          },
          {
            path: 'settings',
            loadComponent: () =>
              import('./pages/project-settings/project-settings.page').then(m => m.ProjectSettingsPage),
          },
          {
            path: 'piled/settings',
            loadComponent: () => import('./modules/piled/piled-settings/piled-settings.page').then(m => m.PiledSettingsPage),
          },
          {
            path: 'piled/reports',
            loadComponent: () => import('./modules/piled/reports/reports.page').then(m => m.ReportsPage),
          },
          {
            path: 'piled/qc',
            loadComponent: () => import('./modules/piled/qc-interface/qc-interface.page').then(m => m.QCInterfacePage),
          },
          {
            path: 'piled/operator',
            canActivate: [piledOperatorRoleGuard],
            loadComponent: () => import('./modules/piled/operator-interface/operator-interface.page').then(m => m.OperatorInterfacePage),
            children: [
              {
                path: 'todays-pilings',
                outlet: 'sidenav',
                loadComponent: () => import('./modules/piled/operator-interface/todays-pilings-sidenav/todays-pilings-sidenav.component').then(m => m.TodaysPilingsSidenavComponent)
              }
            ]
          },
          {
            path: 'piled/dashboard',
            loadComponent: () => import('./modules/piled/project-dashboard/project-dashboard.component').then(m => m.ProjectDashboardComponent),
          },
          {
            path: 'pull-testing/setup',
            canActivate: [pullTestingSetupRoleGuard],
            loadComponent: () =>
              import('./modules/pull-testing/pull-testing-setup-wizard/pull-testing-setup-wizard.page').then(
                m => m.PullTestingSetupWizardPage
              ),
          },
          {
            path: 'pull-testing/install',
            canActivate: [pullTestingOperatorRoleGuard],
            loadComponent: () =>
              import('./modules/pull-testing/pull-testing-install/pull-testing-install.page').then(m => m.PullTestingInstallPage),
            children: [
              {
                path: 'todays-pull-test-pilings',
                outlet: 'sidenav',
                loadComponent: () =>
                  import(
                    './modules/pull-testing/pull-test-todays-pilings-sidenav/pull-test-todays-pilings-sidenav.component'
                  ).then(m => m.PullTestTodaysPilingsSidenavComponent),
              },
            ],
          },
          {
            path: 'pull-testing/test',
            canActivate: [pullTestingOperatorRoleGuard],
            loadComponent: () =>
              import('./modules/pull-testing/pull-testing-test/pull-testing-test.page').then(m => m.PullTestingTestPage),
          },
          {
            path: 'pull-testing/report',
            canActivate: [pullTestingReportRoleGuard],
            loadComponent: () =>
              import('./modules/pull-testing/pull-testing-generate-report/pull-testing-generate-report.page').then(
                m => m.PullTestingGenerateReportPage
              ),
          },
          {
            path: 'pull-testing/event-log',
            canActivate: [pullTestingReportRoleGuard],
            loadComponent: () =>
              import('./modules/pull-testing/pull-testing-event-log/pull-testing-event-log.page').then(
                m => m.PullTestingEventLogPage
              ),
          },
          {
            path: 'pull-testing',
            loadComponent: () => import('./modules/pull-testing/pull-testing-dashboard/pull-testing-dashboard.page').then(m => m.PullTestingDashboardPage),
          },
        ],
      },
      {
        path: 'request-project',
        loadComponent: () => import('./pages/request-project/request-project.page').then(m => m.RequestProjectPage),
      },
      {
        path: 'create-project',
        loadComponent: () => import('./pages/create-project/create-project.page').then(m => m.CreateProjectPage),
      },
      {
        path: 'request-user',
        loadComponent: () => import('./pages/request-user/request-user.page').then(m => m.RequestUserPage),
      },
      {
        path: 'version-test',
        loadComponent: () => import('./pages/version-test/version-test.page').then(m => m.VersionTestPage)
      },
    ]
  },
  {
    path: '**',
    redirectTo: 'login'
  },
];
